import { request } from './client';

/**
 * Always hits the real backend, even in mock mode.
 * Resolves to `{ status, message, data: { userId, email, name, token } }`.
 */
export function login(email, password) {
  return request('auth/admin/login', { method: 'POST', body: { email, password } });
}

/*
 * Password reset (public endpoints, no token). Envelope: { Success, Message, Data }.
 * Errors reject with ApiError; `err.data.Data` holds extra details such as `attemptsLeft`.
 * Never log the OTP, reset token or passwords.
 */

/**
 * POST auth/forgot-password. Answers the same for unknown emails (no account enumeration).
 * 400 invalid email, 503 the email couldn't be sent.
 * @returns {Promise<{ message: string, expiresInSeconds: number, resendAfterSeconds: number }>}
 */
export async function requestPasswordReset(email) {
  const res = await request('auth/forgot-password', { method: 'POST', body: { email }, auth: false });
  return {
    message: res?.Message,
    expiresInSeconds: Number(res?.Data?.expiresInSeconds) || 600,
    resendAfterSeconds: Number(res?.Data?.resendAfterSeconds) || 60,
  };
}

/**
 * POST auth/verify-reset-otp with the 6-digit code as a string (leading zeros kept).
 * 400 + Data.attemptsLeft wrong code; 400 without it no valid/expired code; 429 locked.
 * @returns {Promise<{ resetToken: string, expiresInSeconds: number }>}
 */
export async function verifyResetOtp(email, otp) {
  const res = await request('auth/verify-reset-otp', { method: 'POST', body: { email, otp }, auth: false });
  return { resetToken: res?.Data?.resetToken, expiresInSeconds: Number(res?.Data?.expiresInSeconds) || 900 };
}

/**
 * POST auth/update-password. 400 for validation errors, a reused/expired session,
 * or a new password equal to the current one.
 * @returns {Promise<{ message: string }>}
 */
export async function updatePassword(resetToken, newPassword, confirmPassword) {
  const res = await request('auth/update-password', {
    method: 'POST',
    body: { resetToken, newPassword, confirmPassword },
    auth: false,
  });
  return { message: res?.Message };
}
